
import React, { useMemo, useState, useRef, useEffect } from 'react';
import { X, ShoppingBag, ArrowUpRight, ArrowDownRight, ChevronUp, ChevronDown, ChevronsUpDown, BarChart3, LayoutGrid, Info, SortAsc, SortDesc, Hash, Tag, DollarSign, Target, Calendar } from 'lucide-react';
import { Transaction } from '../types';

interface LatestBuysModalProps {
  isOpen: boolean;
  onClose: () => void;
  transactions: Transaction[];
  currentPrices: Record<string, { price: number; change24h: number }>;
  theme: 'dark' | 'light';
  icons?: Record<string, string>;
  visibleSymbols?: string[];
}

type SortKey = 'rank' | 'price' | 'quantity' | 'total' | 'currentVal' | 'timestamp' | 'pnlPerc';
type SortOrder = 'asc' | 'desc' | null;
type ViewMode = 'table' | 'histogram';

interface SortConfig {
  key: SortKey;
  order: SortOrder;
}

const LatestBuysModal: React.FC<LatestBuysModalProps> = ({ isOpen, onClose, transactions, currentPrices, theme, icons = {}, visibleSymbols }) => {
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: 'rank', order: 'asc' });
  const [viewMode, setViewMode] = useState<ViewMode>('table');
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    if (containerRef.current) {
      const observer = new ResizeObserver((entries) => {
        if (entries[0]) {
          setContainerWidth(entries[0].contentRect.width);
        }
      });
      observer.observe(containerRef.current);
      return () => observer.disconnect();
    }
  }, [viewMode, isOpen]);

  const latestBuys = useMemo(() => {
    const lastPurchaseByTicket: Record<string, Transaction> = {};
    const sortedTxsByTime = [...transactions].sort((a, b) => a.timestamp - b.timestamp);
    
    sortedTxsByTime.forEach(t => {
      if (visibleSymbols && !visibleSymbols.includes(t.ticket)) return;
      const status = t.status.toLowerCase();
      if (status.includes('compra') || status.includes('buy')) {
        lastPurchaseByTicket[t.ticket] = t;
      }
    });

    const results = Object.values(lastPurchaseByTicket).map(t => {
      const binanceKey = t.ticket.replace(/[-/]/g, '').replace('USD', 'USDT');
      const stockKey = t.ticket.replace(/[-/]/g, '').replace('USD', '').replace('USDT', '');
      
      const priceData = currentPrices[t.ticket] || currentPrices[binanceKey] || currentPrices[stockKey];
      const livePrice = priceData?.price || 0;
      
      const currentVal = t.quantity * livePrice;
      const pnl = currentVal - t.total;
      const pnlPerc = t.total > 0 ? (pnl / t.total) * 100 : 0;
      return { ...t, currentVal, pnl, pnlPerc };
    });

    if (!sortConfig.key || !sortConfig.order) return results;

    const topRanks: Record<string, number> = { 'BTC': 1, 'ETH': 2, 'BNB': 3, 'SOL': 4, 'AVAX': 5, 'DOT': 6 };

    return [...results].sort((a, b) => {
      let valA: any, valB: any;
      switch (sortConfig.key) {
        case 'rank':
          valA = topRanks[a.ticket.split('-')[0].toUpperCase()] || 999;
          valB = topRanks[b.ticket.split('-')[0].toUpperCase()] || 999;
          break;
        case 'price': valA = a.price; valB = b.price; break;
        case 'quantity': valA = a.quantity; valB = b.quantity; break;
        case 'total': valA = a.total; valB = b.total; break;
        case 'currentVal': valA = a.currentVal; valB = b.currentVal; break;
        case 'timestamp': valA = a.timestamp; valB = b.timestamp; break;
        case 'pnlPerc': valA = a.pnlPerc; valB = b.pnlPerc; break;
        default: valA = 0; valB = 0;
      }
      return sortConfig.order === 'asc' ? (valA > valB ? 1 : -1) : (valA < valB ? 1 : -1);
    });
  }, [transactions, currentPrices, sortConfig, visibleSymbols]);

  const decimalPlaces = latestBuys.length > 20 ? 0 : 1;

  if (!isOpen) return null;

  const getIconUrl = (symbol: string) => icons[symbol.split('-')[0].toUpperCase()] || '';

  const padding = { top: 100, right: 40, bottom: 120, left: 70 };
  const chartHeight = 550;
  const availableWidth = Math.max(containerWidth - padding.left - padding.right, 400);
  const totalItems = latestBuys.length || 1;

  // Lógica para limitar el ancho de las barras y centrar el contenido
  const MAX_BAR_WIDTH = 100;
  const PREFERRED_GAP = 50;

  let barWidth = (availableWidth * 0.7) / totalItems;
  let gap = (availableWidth * 0.3) / (totalItems + 1);

  if (barWidth > MAX_BAR_WIDTH) {
      barWidth = MAX_BAR_WIDTH;
      gap = PREFERRED_GAP;
  }

  const totalChartContentWidth = (totalItems * barWidth) + ((totalItems + 1) * gap);
  const centerOffset = Math.max(0, (availableWidth - totalChartContentWidth) / 2);

  const maxAbsROI = Math.max(...latestBuys.map(d => Math.abs(d.pnlPerc)), 10);
  const scaleLimit = maxAbsROI * 1.35;

  const getY = (val: number) => {
    const plotHeight = chartHeight - padding.top - padding.bottom;
    const ratio = (val - (-scaleLimit)) / (scaleLimit * 2);
    return (padding.top + plotHeight) - (ratio * plotHeight);
  };

  const zeroY = getY(0);

  const themeClasses = {
    overlay: "fixed inset-0 bg-black/80 backdrop-blur-md z-[120] flex items-center justify-center p-4 animate-in fade-in duration-300",
    container: `${theme === 'dark' ? 'bg-[#0b0c10] border-[#20232a]' : 'bg-white border-slate-200'} border w-full max-w-[1280px] h-[88vh] rounded-[3rem] shadow-2xl overflow-hidden flex flex-col animate-in zoom-in-95 duration-200`,
    header: `flex items-center justify-between p-7 border-b ${theme === 'dark' ? 'border-[#20232a]' : 'border-slate-100'}`,
    tableHeader: `${theme === 'dark' ? 'bg-[#161b22] text-slate-400' : 'bg-slate-50 text-slate-500'} text-xs font-black uppercase tracking-wider sticky top-0 z-10`,
    cell: `px-4 py-4 text-base font-medium border-b ${theme === 'dark' ? 'border-[#161b22]' : 'border-slate-50'}`,
    textMain: theme === 'dark' ? 'text-white' : 'text-slate-900',
    tooltip: theme === 'dark' ? 'bg-[#1a1c22]/98 border-[#363a45] text-white shadow-[0_20px_50px_rgba(0,0,0,0.6)]' : 'bg-white/98 border-slate-200 text-slate-900 shadow-xl',
    border: theme === 'dark' ? 'border-[#20232a]' : 'border-slate-100',
  };

  const getTooltipStyle = (index: number) => {
    if (index === null || !latestBuys[index]) return {};
    const item = latestBuys[index];
    const xPos = padding.left + centerOffset + gap + index * (barWidth + gap) + barWidth / 2;
    const isPositive = item.pnlPerc >= 0;
    const anchorROI = isPositive ? -5 : 5;
    const yAnchor = getY(anchorROI);
    const verticalTranslate = isPositive ? '0%' : '-100%';

    return {
      left: `${xPos}px`,
      top: `${yAnchor}px`,
      transform: `translateX(-50%) translateY(${verticalTranslate})`
    };
  };

  const forceSort = (key: SortKey, order: SortOrder) => setSortConfig({ key, order });

  return (
    <div className={themeClasses.overlay} onClick={onClose}>
      <div className={themeClasses.container} onClick={e => e.stopPropagation()}>
        <div className={themeClasses.header}>
          <div className="flex items-center space-x-5">
            <div className="bg-emerald-600 p-3.5 rounded-2xl shadow-xl shadow-emerald-600/20">
              <ShoppingBag size={28} className="text-white" />
            </div>
            <div>
              <h2 className={`text-2xl font-black ${themeClasses.textMain} tracking-tight uppercase`}>Últimas Compras Realizadas</h2>
              <p className="text-slate-500 text-sm font-bold uppercase tracking-widest">Filtrado según vista actual de activos</p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
             <div className={`flex p-1.5 rounded-2xl border ${theme === 'dark' ? 'bg-white/5 border-white/10' : 'bg-slate-100 border-slate-200'}`}>
                <button onClick={() => setViewMode('table')} className={`p-2.5 rounded-xl transition-all ${viewMode === 'table' ? 'bg-indigo-600 text-white shadow-md' : 'text-slate-500 hover:text-slate-300'}`}><LayoutGrid size={22} /></button>
                <button onClick={() => setViewMode('histogram')} className={`p-2.5 rounded-xl transition-all ${viewMode === 'histogram' ? 'bg-indigo-600 text-white shadow-md' : 'text-slate-500 hover:text-slate-300'}`}><BarChart3 size={22} /></button>
             </div>
             <button onClick={onClose} className={`p-2.5 rounded-full ${theme === 'dark' ? 'hover:bg-white/10' : 'hover:bg-slate-100'} transition-colors`}><X size={30} className={themeClasses.textMain} /></button>
          </div>
        </div>

        <div className="flex-1 overflow-auto custom-scrollbar relative" ref={scrollRef}>
          {viewMode === 'table' ? (
            <div className="p-4" ref={containerRef}>
                <table className="w-full text-left border-separate border-spacing-0">
                  <thead className={themeClasses.tableHeader}>
                    <tr>
                      <th className="px-6 py-5 rounded-tl-2xl"># / Activo</th>
                      <th className="px-4 py-5">Precio Compra</th>
                      <th className="px-4 py-5">Cant.</th>
                      <th className="px-4 py-5">Inversión</th>
                      <th className="px-4 py-5">Valor Actual</th>
                      <th className="px-6 py-5 text-right rounded-tr-2xl">Fecha</th>
                    </tr>
                  </thead>
                  <tbody className={theme === 'dark' ? 'divide-[#161b22]' : 'divide-slate-50'}>
                    {latestBuys.map((t, idx) => (
                      <tr key={t.id} className={`${theme === 'dark' ? 'hover:bg-[#161b22]' : 'hover:bg-slate-50/50'} transition-colors`}>
                        <td className="px-6 py-5">
                          <div className="flex items-center space-x-5">
                            <span className="text-sm font-mono text-slate-500 w-5">{idx + 1}</span>
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-black relative flex-shrink-0 overflow-hidden ${theme === 'dark' ? 'bg-indigo-500/20 text-indigo-400' : 'bg-indigo-100 text-indigo-600'}`}>
                              <span className="uppercase">{t.ticket.charAt(0)}</span>
                              {getIconUrl(t.ticket) && <img src={getIconUrl(t.ticket)} className="absolute inset-0 w-full h-full object-contain" alt="" onError={(e) => (e.currentTarget.style.display = 'none')} />}
                            </div>
                            <div className="flex flex-col">
                               <span className={`font-black text-lg ${themeClasses.textMain}`}>{t.symbolName}</span>
                               <span className="text-xs font-bold text-slate-500 uppercase">{t.ticket.split('-')[0]}</span>
                            </div>
                          </div>
                        </td>
                        <td className={`${themeClasses.cell} font-mono font-bold text-indigo-400`}>$ {t.price.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                        <td className={`${themeClasses.cell} font-mono text-slate-500`}>{t.quantity.toLocaleString(undefined, { maximumFractionDigits: 6 })}</td>
                        <td className={`${themeClasses.cell} font-mono font-black`}>$ {t.total.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                        <td className={themeClasses.cell}>
                          <div className="flex flex-col">
                            <span className={`font-mono font-black text-lg ${t.pnl >= 0 ? 'text-[#089981]' : 'text-[#f23645]'}`}>$ {t.currentVal.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                            <div className={`flex items-center text-xs font-bold ${t.pnl >= 0 ? 'text-[#089981]/70' : 'text-[#f23645]/70'}`}>
                              {t.pnl >= 0 ? <ArrowUpRight size={14} className="mr-1" /> : <ArrowDownRight size={14} className="mr-1" />}
                              {t.pnl >= 0 ? '+' : ''}{t.pnlPerc.toFixed(decimalPlaces)}%
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-5 text-right border-b border-transparent">
                          <div className="flex flex-col text-sm">
                              <span className={`font-bold ${themeClasses.textMain}`}>{t.date}</span>
                              <span className="opacity-50 font-mono text-xs">{t.time}</span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
            </div>
          ) : (
            <div className="h-full w-full flex items-center justify-center overflow-visible relative" ref={containerRef}>
                <svg width={availableWidth + padding.left + padding.right} height={chartHeight} className="overflow-visible mx-auto">
                    {[0.5, 1.0].map(mult => [mult, -mult]).flat().map(tick => {
                        const val = scaleLimit * tick;
                        const y = getY(val);
                        return (
                            <React.Fragment key={tick}>
                                <line x1={padding.left} y1={y} x2={availableWidth + padding.left} y2={y} stroke={theme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'} strokeDasharray="6 6" />
                                <text x={padding.left - 15} y={y + 4} textAnchor="end" className="text-[10px] fill-slate-500 font-mono font-black">{val > 0 ? '+' : ''}{Math.round(val)}%</text>
                            </React.Fragment>
                        );
                    })}
                    <line x1={padding.left} y1={zeroY} x2={availableWidth + padding.left} y2={zeroY} stroke={theme === 'dark' ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.2)'} strokeWidth="1.5" />
                    
                    {latestBuys.map((t, i) => {
                        const x = padding.left + centerOffset + gap + i * (barWidth + gap);
                        const isPositive = t.pnlPerc >= 0;
                        const barHeight = Math.max(Math.abs(getY(t.pnlPerc) - zeroY), 2);
                        const y = isPositive ? getY(t.pnlPerc) : zeroY;
                        const isHovered = hoveredIndex === i;
                        const fontSize = Math.min(14, Math.max(10, barWidth * 0.6));
                        const iconZoneY = chartHeight - padding.bottom;
                        const iconSize = Math.min(28, barWidth);

                        return (
                            <g key={t.id} className="transition-all duration-300">
                                <rect x={x} y={y} width={barWidth} height={barHeight} rx={barWidth > 10 ? 6 : 2} fill={isPositive ? '#10b981' : '#f43f5e'} opacity={isHovered ? 1 : 0.8} className="transition-all duration-300" />
                                {barWidth > 20 && (
                                    <text x={x + barWidth/2} y={isPositive ? y - 15 : y + barHeight + 25} textAnchor="middle" className={`font-black font-mono transition-all ${isPositive ? 'fill-[#10b981]' : 'fill-[#f43f5e]'}`} style={{ fontSize: `${fontSize}px` }}>{isPositive ? '+' : ''}{t.pnlPerc.toFixed(decimalPlaces)}%</text>
                                )}
                                <g onMouseEnter={() => setHoveredIndex(i)} onMouseLeave={() => setHoveredIndex(null)} className="cursor-pointer group">
                                    <rect x={x - gap/2} y={iconZoneY} width={barWidth + gap} height={padding.bottom} fill="transparent" />
                                    {barWidth > 12 && (
                                        <foreignObject x={x + barWidth/2 - iconSize/2} y={iconZoneY + 15} width={iconSize} height={iconSize}>
                                            <div className={`w-full h-full rounded-full flex items-center justify-center bg-indigo-500/10 text-indigo-400 font-black relative overflow-hidden transition-transform duration-300 ${isHovered ? 'scale-125' : ''}`} style={{ fontSize: `${iconSize*0.4}px` }}>
                                                <span className="uppercase">{t.ticket.charAt(0)}</span>
                                                <img 
                                                  src={getIconUrl(t.ticket)} 
                                                  className="absolute inset-0 w-full h-full object-contain" 
                                                  onError={(e) => (e.currentTarget.style.display = 'none')}
                                                />
                                            </div>
                                        </foreignObject>
                                    )}
                                    {barWidth > 20 && (
                                        <text x={x + barWidth/2} y={iconZoneY + 58} textAnchor="middle" className={`font-black uppercase tracking-widest transition-colors duration-300 ${isHovered ? 'fill-indigo-500' : (theme === 'dark' ? 'fill-slate-400' : 'fill-slate-600')}`} style={{ fontSize: `${fontSize-1}px` }}>{t.ticket.split('-')[0]}</text>
                                    )}
                                </g>
                            </g>
                        );
                    })}
                </svg>

                {hoveredIndex !== null && latestBuys[hoveredIndex] && (
                    <div 
                        className={`absolute pointer-events-none p-2 rounded-[0.8rem] border shadow-2xl backdrop-blur-xl flex flex-col space-y-0.5 z-[200] animate-in fade-in zoom-in-95 duration-150 ${themeClasses.tooltip}`}
                        style={getTooltipStyle(hoveredIndex)}
                    >
                        <div className="flex items-center space-x-1.5 border-b border-white/5 pb-1 mb-0.5">
                             <div className="w-4 h-4 rounded-full bg-white/5 flex items-center justify-center relative overflow-hidden">
                                <span className="text-[7px] font-black text-indigo-400 uppercase">{latestBuys[hoveredIndex].ticket.charAt(0)}</span>
                                <img src={getIconUrl(latestBuys[hoveredIndex].ticket)} className="absolute inset-0 w-full h-full object-contain" alt="" onError={(e) => e.currentTarget.style.display = 'none'} />
                             </div>
                             <span className="text-[9px] font-black uppercase tracking-widest leading-none">{latestBuys[hoveredIndex].symbolName}</span>
                        </div>
                        <div className="space-y-0.5 min-w-[110px]">
                            <div className="flex justify-between items-center text-[8px]">
                                <span className="opacity-40 font-bold uppercase tracking-tighter">Compra</span>
                                <span className="font-mono font-black text-indigo-400">$ {latestBuys[hoveredIndex].price.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                            </div>
                            <div className="flex justify-between items-center text-[8px]">
                                <span className="opacity-40 font-bold uppercase tracking-tighter">Inversión</span>
                                <span className="font-mono font-bold">$ {latestBuys[hoveredIndex].total.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                            </div>
                            <div className="flex justify-between items-center text-[8px]">
                                <span className="opacity-40 font-bold uppercase tracking-tighter">Valor</span>
                                <span className="font-mono font-bold">$ {latestBuys[hoveredIndex].currentVal.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                            </div>
                            <div className="pt-0.5 mt-0.5 border-t border-white/5 flex justify-between items-center">
                                <span className="text-[7px] font-black opacity-50 uppercase">ROI</span>
                                <span className={`font-mono font-black text-[10px] ${latestBuys[hoveredIndex].pnlPerc >= 0 ? 'text-emerald-500' : 'text-rose-500'}`}>
                                    {latestBuys[hoveredIndex].pnlPerc >= 0 ? '+' : ''}{latestBuys[hoveredIndex].pnlPerc.toFixed(decimalPlaces)}%
                                </span>
                            </div>
                        </div>
                    </div>
                )}
            </div>
          )}
        </div>

        <div className={`p-7 border-t ${themeClasses.border} flex items-center justify-between bg-black/10`}>
            <div className="flex items-center space-x-12 text-sm font-black uppercase tracking-widest text-slate-500">
                <div className="flex items-center space-x-4"><div className="w-5 h-5 rounded-lg bg-emerald-500 shadow-lg shadow-emerald-500/20"></div><span>ROI Positivo</span></div>
                <div className="flex items-center space-x-4"><div className="w-5 h-5 rounded-lg bg-rose-500 shadow-lg shadow-rose-500/20"></div><span>ROI Negativo</span></div>
            </div>
            <div className="flex items-center space-x-5">
                <div className={`flex p-1 rounded-2xl border ${theme === 'dark' ? 'bg-white/5 border-white/10' : 'bg-slate-200 border-slate-300'}`}>
                    <button onClick={() => forceSort('pnlPerc', 'desc')} className={`px-5 py-2.5 rounded-xl flex items-center space-x-2 text-xs font-black uppercase tracking-tighter transition-all ${sortConfig.key === 'pnlPerc' && sortConfig.order === 'desc' ? 'bg-indigo-600 text-white shadow-md' : 'text-slate-500 hover:text-slate-700'}`} title="Mejores"><SortDesc size={18} /><span className="hidden sm:inline">Mejores</span></button>
                    <button onClick={() => forceSort('pnlPerc', 'asc')} className={`px-5 py-2.5 rounded-xl flex items-center space-x-2 text-xs font-black uppercase tracking-tighter transition-all ${sortConfig.key === 'pnlPerc' && sortConfig.order === 'asc' ? 'bg-indigo-600 text-white shadow-md' : 'text-slate-500 hover:text-slate-700'}`} title="Peores"><SortAsc size={18} /><span className="hidden sm:inline">Peores</span></button>
                    <button onClick={() => forceSort('timestamp', 'desc')} className={`px-5 py-2.5 rounded-xl flex items-center space-x-2 text-xs font-black uppercase tracking-tighter transition-all ${sortConfig.key === 'timestamp' && sortConfig.order === 'desc' ? 'bg-indigo-600 text-white shadow-md' : 'text-slate-500 hover:text-slate-700'}`} title="Más Recientes"><Calendar size={18} /><span className="hidden sm:inline">Recientes</span></button>
                    <button onClick={() => forceSort('rank', 'asc')} className={`px-5 py-2.5 rounded-xl flex items-center space-x-2 text-xs font-black uppercase tracking-tighter transition-all ${sortConfig.key === 'rank' ? 'bg-indigo-600 text-white shadow-md' : 'text-slate-500 hover:text-slate-700'}`} title="Orden Ranking"><Hash size={18} /><span className="hidden sm:inline">Ranking</span></button>
                </div>
                <div className="w-[1px] h-8 bg-slate-700/50 mx-2" />
                <div className="flex items-center space-x-3 text-slate-500 bg-white/5 px-4 py-2 rounded-xl border border-white/5">
                    <Info size={18} className="text-indigo-400" />
                    <span className="text-[11px] font-black uppercase tracking-widest">Análisis por Activo</span>
                </div>
            </div>
        </div>
      </div>
    </div>
  );
};

export default LatestBuysModal;
